import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { coverFor } from "@/lib/covers";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Search } from "lucide-react";

const categories = ["All", "AI", "Work", "Education", "Skills", "Economy", "Policy", "Leadership"];

const categoryColors = {
  "AI": "bg-violet-600/20 text-violet-300",
  "Education": "bg-emerald-600/20 text-emerald-300",
  "Work": "bg-blue-600/20 text-blue-300",
  "Policy": "bg-amber-600/20 text-amber-300",
  "Economy": "bg-rose-600/20 text-rose-300",
  "Skills": "bg-cyan-600/20 text-cyan-300",
  "Leadership": "bg-orange-600/20 text-orange-300",
};

export const articles = [
  { category: "AI", title: "AI Agents Move from Assistants to Economic Actors", date: "Oct 13, 2026", readTime: "5 min", slug: "ai-agents-economic-actors", summary: "AI agents are beginning to perform economic roles with measurable value across industries." },
  { category: "Education", title: "What Schools Should Be Teaching Now for an AI Economy", date: "Oct 11, 2026", readTime: "6 min", slug: "schools-ai-economy", summary: "The curriculum gap between traditional education and AI-economy readiness continues to widen." },
  { category: "Work", title: "The Entry-Level Career Ladder Is Being Rewritten", date: "Oct 12, 2026", readTime: "5 min", slug: "entry-level-career-ladder", summary: "Organizations are redesigning talent development as AI takes on early-career tasks." },
  { category: "Policy", title: "Global Governments Accelerate AI Workforce Strategies", date: "Oct 13, 2026", readTime: "6 min", slug: "governments-ai-workforce", summary: "From the EU to Southeast Asia, governments race to develop workforce transformation policies." },
  { category: "AI", title: "From Tools to Teammates: The Next Phase of AI Agents", date: "Oct 12, 2026", readTime: "6 min", slug: "tools-to-teammates", summary: "The latest AI agents demonstrate unprecedented autonomy in collaborative work settings." },
  { category: "Economy", title: "The New Geography of Opportunity in the Age of AI", date: "Oct 10, 2026", readTime: "5 min", slug: "geography-of-opportunity", summary: "Remote work and digital economy shifts redraw the map of economic opportunity." },
  { category: "Education", title: "Universities Rethink College ROI in the Age of AI", date: "Oct 13, 2026", readTime: "5 min", slug: "universities-rethink-roi", summary: "New data shows growing momentum for skills-based, modular and hybrid learning pathways." },
  { category: "Skills", title: "The Skills That Will Matter Most by 2030", date: "Oct 9, 2026", readTime: "7 min", slug: "skills-2030", summary: "A framework for understanding which human capabilities become more valuable as AI advances." },
];

const FILTER_MAP = { "Artificial Intelligence": "AI", "Responsible AI": "AI", "Adaptive Intelligence": "AI", "Future of Work": "Work", "Workforce": "Work" };
const API = process.env.REACT_APP_BACKEND_URL;

const readTime = (a) => {
  const words = [a.what_happened, a.why_it_matters, a.raulvins_signal, a.body].join(" ").split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 220))} min`;
};

const fromCms = (a) => ({
  category: FILTER_MAP[a.category] || a.category || "AI",
  label: a.category,
  title: a.title,
  date: a.published_date ? new Date(a.published_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "",
  readTime: readTime(a),
  slug: a.slug,
  summary: a.excerpt || a.subtitle || a.what_happened,
  image: coverFor(a),
});

const withCovers = (list) => list.map((a) => ({ ...a, image: a.image || coverFor(a) }));

const Intelligence = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [cms, setCms] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/articles`).then((r) => (r.ok ? r.json() : [])).then((d) => setCms(d.map(fromCms))).catch(() => {});
  }, []);

  const cmsSlugs = new Set(cms.map((a) => a.slug));
  const all = withCovers([...cms, ...articles.filter((a) => !cmsSlugs.has(a.slug))]);

  const filtered = all.filter((a) => {
    const matchCat = activeCategory === "All" || a.category === activeCategory;
    const matchSearch = a.title.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <main className="pt-24 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-sm uppercase tracking-[0.25em] text-[var(--accent-violet-light)] font-medium mb-4">Intelligence</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white italic mb-4" data-testid="intelligence-heading">
            Future Signals
          </h1>
          <p className="text-[var(--text-secondary)] text-lg max-w-2xl">
            The developments shaping AI, work, education and human opportunity before they become conventional wisdom.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeCategory === cat
                  ? "bg-[var(--accent-violet)] text-white"
                  : "bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-white border-glow"
              }`}
              data-testid={`filter-${cat.toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-[var(--bg-card)] border-glow rounded-lg text-sm text-white placeholder:text-[var(--text-secondary)] focus:border-[var(--accent-violet)] focus:outline-none w-48"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((article, i) => (
            <motion.div
              key={article.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                to={`/intelligence/${article.slug}`}
                className="block h-full bg-[var(--bg-card)] border-glow rounded-xl overflow-hidden card-hover group"
                data-testid={`article-card-${article.slug}`}
              >
                <div className="aspect-video bg-gradient-to-br from-[var(--bg-secondary)] to-[var(--bg-card)] relative overflow-hidden">
                  <img src={article.image} alt={article.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" data-testid={`article-cover-${article.slug}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] via-transparent to-violet-900/10" />
                  <div className="absolute top-4 left-4">
                    <span className={`category-badge ${categoryColors[article.category] || "bg-violet-600/20 text-violet-300"}`}>
                      {article.label || article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold text-white mb-2 group-hover:text-[var(--accent-violet-light)] transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)] mb-3">
                    <span>{article.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{article.readTime}</span>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed line-clamp-2">{article.summary}</p>
                  <div className="mt-4">
                    <ArrowRight className="w-4 h-4 text-[var(--accent-violet-light)] group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default Intelligence;
